import { Card, CardContent } from "@/components/ui/card";
import {
  TRANSFER_STAT_CARD,
  TRANSFER_STAT_CARD_BODY,
  TRANSFER_STAT_GRID,
} from "@/modules/inventory/presentation/ui/transfers/transferCardLayout";
import type { TransferReportSummary } from "@/modules/reports/domain/repo/transfer/get-transfer-report-summary";
import {
  AlertTriangleIcon,
  ClockIcon,
  PackageIcon,
  TruckIcon,
} from "lucide-react";

type Props = {
  summary?: TransferReportSummary;
  isLoading?: boolean;
};

function fmt(n: number) {
  return n.toLocaleString("lo-LA", { maximumFractionDigits: 2 });
}

export function TransferReportsSummaryCards({ summary, isLoading }: Props) {
  if (isLoading) {
    return (
      <div className={TRANSFER_STAT_GRID}>
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className={TRANSFER_STAT_CARD}>
            <CardContent className={TRANSFER_STAT_CARD_BODY}>
              <div className="h-10 w-full animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const s = summary;
  const fullPct =
    s && s.linesWithShipped > 0
      ? Math.round(s.fullReceiveRate * 100)
      : 0;

  return (
    <div className={TRANSFER_STAT_GRID}>
      <Card className={TRANSFER_STAT_CARD}>
        <CardContent className={TRANSFER_STAT_CARD_BODY}>
          <div className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-lg">
            <PackageIcon className="text-primary size-5 opacity-90" />
          </div>
          <div className="min-w-0">
            <p className="text-muted-foreground text-sm">ຄຳຂໍໃນຊ່ວງ</p>
            <p className="font-semibold text-2xl tabular-nums">
              {fmt(s?.requestCount ?? 0)}
            </p>
            <p className="text-muted-foreground text-xs">
              {fmt(s?.qtyShipped ?? 0)} ສົ່ງ · {fmt(s?.qtyReceived ?? 0)} ຮັບ
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className={`${TRANSFER_STAT_CARD} border-amber-500/20`}>
        <CardContent className={TRANSFER_STAT_CARD_BODY}>
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
            <ClockIcon className="size-5 text-amber-600" />
          </div>
          <div className="min-w-0">
            <p className="text-muted-foreground text-sm">ຄິວຄ້າງ (ປັດຈຸບັນ)</p>
            <p className="font-semibold text-2xl tabular-nums">
              {fmt(s?.submittedPending ?? 0)}
            </p>
            <p className="text-muted-foreground text-xs">ລໍຖ້າອະນຸມັດ</p>
          </div>
        </CardContent>
      </Card>

      <Card className={`${TRANSFER_STAT_CARD} border-blue-500/20`}>
        <CardContent className={TRANSFER_STAT_CARD_BODY}>
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
            <TruckIcon className="size-5 text-blue-600" />
          </div>
          <div className="min-w-0">
            <p className="text-muted-foreground text-sm">ສົ່ງແລ້ວຍັງບໍ່ຮັບ</p>
            <p className="font-semibold text-2xl tabular-nums">
              {fmt(s?.shippedUnreceived ?? 0)}
            </p>
            <p className="text-muted-foreground text-xs">ສະຖານະ SHIPPED</p>
          </div>
        </CardContent>
      </Card>

      <Card className={TRANSFER_STAT_CARD}>
        <CardContent className={TRANSFER_STAT_CARD_BODY}>
          <div className="bg-muted flex size-10 shrink-0 items-center justify-center rounded-lg">
            <AlertTriangleIcon className="text-muted-foreground size-5" />
          </div>
          <div className="min-w-0">
            <p className="text-muted-foreground text-sm">ຄວາມຕ່າງ · ມູນຄ່າສົ່ງ</p>
            <p className="font-semibold text-2xl tabular-nums">
              {fmt(s?.qtyVariance ?? 0)}
            </p>
            <p className="text-muted-foreground text-xs">
              {fullPct}% ຮັບຄົບ ·{" "}
              {(s?.shippedValueLak ?? 0).toLocaleString("lo-LA")} ₭
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
